import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { sleep } from '@cp/utils/sleep';
import { CatalogItemListFilter } from '@common/model/catalog/item/list.filter';
import { dateObjToDateOnlyISOString } from '@cp/shared/lib';
import { parse } from 'date-fns';
import { DATE_PICKER_VALUE_DATE_FORMAT_DEFAULT } from '@cp/ds/src/components/form/components/field/dateTimePicker/const';
import { useTrackEvent } from 'src/shared/tracking';
import { useWatcher } from '../model/hooks/useWatcher';
import { FormFieldName, FormModel, FormSubmitTrigger } from '../model/form';
import { getDefaultValues } from '../model/utils/getDefaultValues';
import { getFieldNames } from '../model/utils/getFieldsNames';
import { boolToNumeric } from '../model/utils/boolToNumeric';
import { FormAvailableOnlyFieldName } from '../../ui/availableOnly/model';
import { CatalogFilterHandle, CatalogFilterProps, CatalogFilterViewProps } from './model';
import { View } from './view';

export const CatalogFilter = React.forwardRef<CatalogFilterHandle, CatalogFilterProps>(
  ({ careerId, catalogType, defaultFilter, onChange, onMapButtonClick, sx, careerDictionary }, ref) => {
    const defaultValues = React.useMemo(() => getDefaultValues(defaultFilter ?? {}), [defaultFilter]);
    const track = useTrackEvent();

    const form = useForm<FormModel>({
      defaultValues: {
        ...defaultValues,
      },
    });

    const submit = React.useCallback<FormSubmitTrigger>(async () => {
      await sleep(0);
      const values = form.getValues();
      const fieldNames = getFieldNames({ careerId, catalogType });
      const scheduleDate = values[FormAvailableOnlyFieldName.Date]
        ? dateObjToDateOnlyISOString(
            parse(values[FormAvailableOnlyFieldName.Date] as string, DATE_PICKER_VALUE_DATE_FORMAT_DEFAULT, new Date()),
          )
        : undefined;
      const scheduleTimeFrom = values[FormAvailableOnlyFieldName.Start] ?? undefined;
      const scheduleTimeTo = values[FormAvailableOnlyFieldName.End] ?? undefined;

      const filterValues = fieldNames.reduce<CatalogItemListFilter>((acc, fieldName) => {
        switch (fieldName) {
          // case FormFieldName.Online:
          // acc.isOnline = boolToNumeric(values[fieldName]);
          // break;
          case FormFieldName.Age:
            acc.age = values[fieldName] ? values[fieldName] : undefined;
            break;
          case FormFieldName.Price:
            acc.priceMin = values[fieldName]?.[0] ?? undefined;
            acc.priceMax = values[fieldName]?.[1] ?? undefined;
            break;
          case FormFieldName.ChildrenCount:
            acc.childrenCount = values[fieldName] ?? undefined;
            break;
          case FormFieldName.Training:
            acc.training = boolToNumeric(values[fieldName]);
            break;
          case FormFieldName.Baby:
            acc.baby = boolToNumeric(values[fieldName]);
            break;
          case FormFieldName.ChildWithDisabilities:
            acc.childWithDisabilities = boolToNumeric(values[fieldName]);
            break;
          case FormFieldName.Options:
            const valuesArray = values[fieldName];
            acc.options = valuesArray?.length ? valuesArray : undefined;
            break;
          case FormAvailableOnlyFieldName.AvailableOnly:
            acc.start = scheduleTimeFrom;
            acc.date = scheduleDate;
            acc.end = scheduleTimeTo;
            break;
          case FormAvailableOnlyFieldName.Date:
            acc.date = scheduleDate;
            break;
          case FormAvailableOnlyFieldName.Start:
            acc.start = scheduleTimeFrom;
            break;
          case FormAvailableOnlyFieldName.End:
            acc.end = scheduleTimeTo;
            break;
          default:
              // eslint-disable-next-line no-undef
            if (__DEV__) {
              throw new Error(`Can not convert form field (name: "${fieldName}") value to filter value. Converting is not implemented!`);
            }
        }
        return acc;
      }, {});

      onChange(filterValues);
    }, [careerId, catalogType, form, onChange]);

    const handleResetFields = React.useCallback<CatalogFilterViewProps['onResetFields']>(
      (fields) => {
        fields.forEach((fieldName) => {
          form.setValue(fieldName, null);
        });
        submit();
      },
      [form, submit],
    );

    React.useImperativeHandle(
      ref,
      () => ({
        reset: () => {
          const fieldNames = getFieldNames({ careerId, catalogType });
          handleResetFields(fieldNames);
        },
      }),
      [careerId, catalogType, handleResetFields],
    );

    useWatcher({ form, defaultValues, submit, careerId, catalogType });

    const viewProps: CatalogFilterViewProps = {
      careerId,
      catalogType,
      form,
      onMapButtonClick,
      sx,
      careerDictionary,
      onResetFields: handleResetFields,
    };

    return (
      <FormProvider {...form}>
        <View {...viewProps} />
      </FormProvider>
    );
  },
);

export default CatalogFilter;
